import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Loader2, Send, Square, Plus, History, MessageSquare } from "lucide-react";
import { cn } from "@/lib/utils";
import { useResearchChatStream, reconcileFromCitations, type ReaderMessage } from "@/hooks/useResearchChatStream";
import { TrustChip } from "@/components/research-hub";

const SUGGESTED = [
  "What's our view on inflation?",
  "Which of our views is the market moving against?",
  "Where are we under-researched in the book?",
  "Why did we hold this view earlier in the year?",
];

interface SessionRow {
  id: string;
  title: string | null;
  last_message_at: string | null;
  message_count: number | null;
}

export default function ResearchAskChat() {
  const [params, setParams] = useSearchParams();
  const { messages, streaming, busy, sessionId, send, stop, reset, setMessages } = useResearchChatStream();
  const [input, setInput] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const prefilledRef = useRef(false);

  // Prefill from an "Ask about this" affordance (never auto-send — the user stays in control).
  useEffect(() => {
    const q = params.get("q");
    if (q && !prefilledRef.current) {
      prefilledRef.current = true;
      setInput(q);
      params.delete("q");
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  useEffect(() => {
    requestAnimationFrame(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }));
  }, [messages]);

  const {
    data: sessions,
    isLoading: sessionsLoading,
    error: sessionsError,
    refetch: refetchSessions,
  } = useQuery({
    queryKey: ["research_chat_sessions"],
    queryFn: async () => {
      const { data } = await supabase
        .from("research_chat_sessions")
        .select("id,title,last_message_at,message_count,is_archived")
        .eq("is_archived", false)
        .order("last_message_at", { ascending: false, nullsFirst: false })
        .limit(40);
      return (data ?? []) as SessionRow[];
    },
  });

  async function loadThread(id: string) {
    const { data } = await supabase
      .from("research_chat_messages")
      .select("role,content,citations,created_at")
      .eq("session_id", id)
      .order("created_at", { ascending: true })
      // Tiebreaker so a turn's two rows can't sort assistant-before-user when timestamps match
      // ('user' > 'assistant', so descending puts the prompt first).
      .order("role", { ascending: false });
    const mapped: ReaderMessage[] = (data ?? []).map((m: any, i: number) => {
      if (m.role === "assistant") {
        const turnKey = `load-${id}-${i}`;
        const { display, sources } = reconcileFromCitations(
          m.content ?? "",
          Array.isArray(m.citations) ? m.citations : [],
          turnKey,
        );
        return { role: "assistant", content: m.content ?? "", display, sources, turnKey, done: true };
      }
      return { role: "user", content: m.content ?? "", done: true };
    });
    reset(id, mapped);
  }

  function onSend() {
    if (!input.trim() || busy) return;
    const q = input.trim();
    setInput("");
    send(q);
  }

  return (
    <div className="flex gap-4 h-[calc(100vh-16rem)] min-h-[28rem]">
      {/* History rail (desktop) */}
      <aside className="hidden lg:flex w-60 shrink-0 flex-col gap-2">
        <Button variant="outline" size="sm" className="justify-start" onClick={() => reset(undefined, [])} disabled={busy}>
          <Plus className="h-4 w-4 mr-2" /> New conversation
        </Button>
        <ThreadList sessions={sessions} activeId={sessionId} onPick={loadThread} loading={sessionsLoading} error={!!sessionsError} onRetry={refetchSessions} disabled={busy} />
      </aside>

      {/* Conversation */}
      <Card className="flex-1 flex flex-col min-w-0">
        <div className="flex items-center justify-between gap-2 px-4 py-2 border-b">
          <span className="font-heading text-sm font-medium truncate">Ask the research hub</span>
          <div className="lg:hidden flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => reset(undefined, [])} disabled={busy}>
              <Plus className="h-4 w-4" />
            </Button>
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm">
                  <History className="h-4 w-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72">
                <SheetHeader>
                  <SheetTitle className="font-heading">Recent conversations</SheetTitle>
                </SheetHeader>
                <div className="mt-4">
                  <ThreadList sessions={sessions} activeId={sessionId} onPick={loadThread} loading={sessionsLoading} error={!!sessionsError} onRetry={refetchSessions} disabled={busy} />
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center gap-4 px-6">
              <MessageSquare className="h-9 w-9 text-muted-foreground" />
              <p className="font-body text-sm text-muted-foreground max-w-md">
                Ask anything about our research in plain English. Every answer is cited.
              </p>
              <div className="flex flex-wrap gap-2 justify-center max-w-lg">
                {SUGGESTED.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    className="rounded-full border px-3 py-1.5 font-body text-xs text-foreground hover:bg-muted transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m, i) => <Bubble key={i} msg={m} />)
          )}
        </div>

        <div className="border-t p-3 space-y-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                e.preventDefault();
                onSend();
              }
            }}
            placeholder="Ask the research hub… (Cmd/Ctrl+Enter to send)"
            rows={2}
            className="resize-none max-h-40 font-body"
            disabled={busy}
          />
          <div className="flex items-center justify-end">
            {streaming ? (
              <Button size="sm" variant="destructive" onClick={stop}>
                <Square className="h-3.5 w-3.5 mr-1" /> Stop
              </Button>
            ) : (
              <Button size="sm" onClick={onSend} disabled={!input.trim() || busy}>
                <Send className="h-3.5 w-3.5 mr-1" /> Send
              </Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

function ThreadList({
  sessions,
  activeId,
  onPick,
  loading,
  error,
  onRetry,
  disabled,
}: {
  sessions: SessionRow[] | undefined;
  activeId: string | undefined;
  onPick: (id: string) => void;
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  disabled?: boolean;
}) {
  if (loading) {
    return (
      <div className="space-y-1">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-7 w-full" />
        ))}
      </div>
    );
  }
  if (error) {
    return (
      <button
        onClick={onRetry}
        className="font-body text-xs text-muted-foreground hover:text-foreground px-1 text-left"
      >
        Couldn't load history. Try again.
      </button>
    );
  }
  if (!sessions || sessions.length === 0) {
    return <p className="font-body text-xs text-muted-foreground px-1">No past conversations yet.</p>;
  }
  return (
    <div className="flex-1 overflow-y-auto space-y-1">
      {sessions.map((s) => (
        <button
          key={s.id}
          onClick={() => onPick(s.id)}
          disabled={disabled}
          className={cn(
            "w-full text-left rounded-md px-2 py-1.5 font-body text-xs truncate transition-colors",
            s.id === activeId ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted",
            disabled && "opacity-50 pointer-events-none",
          )}
        >
          {s.title || "New conversation"}
        </button>
      ))}
    </div>
  );
}

function Bubble({ msg }: { msg: ReaderMessage }) {
  if (msg.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="bg-muted rounded-lg px-3 py-2 font-body text-sm max-w-[80%] whitespace-pre-wrap">{msg.content}</div>
      </div>
    );
  }
  const text = msg.display ?? msg.content;
  return (
    <div className="space-y-2 max-w-[92%]">
      {msg.status && (
        <span className="inline-flex items-center gap-1.5 font-body text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" /> {msg.status}
        </span>
      )}
      {text && (
        <div className="prose prose-sm max-w-none font-body prose-headings:font-heading prose-a:text-primary">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              a: ({ node, ...props }) => <a {...props} className="text-primary no-underline hover:underline" />,
            }}
          >
            {text}
          </ReactMarkdown>
          {!msg.done && <span className="inline-block w-2 h-4 bg-primary animate-pulse ml-0.5 align-middle" />}
        </div>
      )}
      {msg.sources && msg.sources.length > 0 && (
        <div className="pt-1 border-t space-y-1">
          <div className="font-body text-[11px] uppercase tracking-wide text-muted-foreground/70">Sources</div>
          {msg.sources.map((s) => (
            <div key={s.n} id={`hub-src-${msg.turnKey ?? "0"}-${s.n}`} className="flex items-center gap-2 font-body text-xs">
              <span className="text-muted-foreground tabular-nums">[{s.n}]</span>
              <span className="truncate flex-1">{s.title}</span>
              <TrustChip sourceId={s.sourceId} />
            </div>
          ))}
        </div>
      )}
      {msg.error && (
        <div className="font-body text-xs text-destructive border border-destructive/30 bg-destructive/5 rounded px-2 py-1">
          {msg.error}
        </div>
      )}
      {msg.stopped && <div className="font-body text-[11px] text-muted-foreground">Stopped.</div>}
    </div>
  );
}
